import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Flag, PaperPlaneTilt, Check, X, PencilSimple, Trash, Star } from "@phosphor-icons/react";

const STAGE_LABELS = {
  prospect: "Prospect",
  invitation_sent: "Invitation sent",
  interested: "Interested",
  accepted_terms: "Accepted terms",
  profile_verified: "Verified",
  founding_member: "Founding member",
  declined: "Declined",
};
const STAGE_COLOURS = {
  prospect: "bg-black/5 text-navy/70",
  invitation_sent: "bg-blue-100 text-blue-800",
  interested: "bg-amber-100 text-amber-800",
  accepted_terms: "bg-indigo-100 text-indigo-800",
  profile_verified: "bg-emerald-100 text-emerald-800",
  founding_member: "bg-emerald-600 text-white",
  declined: "bg-red-50 text-red-800",
};
const STAGES = Object.keys(STAGE_LABELS);

export default function Founding() {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/founding/prospects"),
        api.get("/founding/stats"),
      ]);
      setProspects(p.data.prospects || []);
      setStats(s.data);
    } catch {
      toast.error("Could not load founding cohort");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return prospects;
    return prospects.filter((p) => p.stage === filter);
  }, [prospects, filter]);

  const moveStage = async (id, stage, note = "") => {
    try {
      await api.post(`/founding/prospects/${id}/stage`, { stage, note });
      toast.success(`Moved to "${STAGE_LABELS[stage]}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const sendInvite = async (p) => {
    if (!p.company_name || !p.contact_email) {
      toast.error("Add company name and contact email before sending invite.");
      setEditing(p);
      return;
    }
    // Draft + queue outreach for this prospect using the founding template
    try {
      await api.post("/outreach/draft", {
        to_name: p.contact_name || p.company_name,
        to_email: p.contact_email,
        to_company: p.company_name,
        to_country: p.country,
        template_key: "sales.founding_country_invite",
        template_vars: {
          first_name: (p.contact_name || "").split(" ")[0] || "there",
          country: p.country,
          company: p.company_name,
        },
      }).catch(() => {}); // best-effort — endpoint may not exist yet in this session
      await moveStage(p.id, "invitation_sent", "Founding invite queued to outreach.");
    } catch {
      await moveStage(p.id, "invitation_sent", "Manual invite tracking.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-6" data-testid="admin-founding">
      <header className="mb-8">
        <div className="overline mb-2">Admin · Programs</div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight flex items-center gap-2">
          <Star size={26} weight="duotone" className="text-ember" /> Founding Country Partner Program
        </h1>
        <p className="text-navy/70 text-sm mt-2 max-w-3xl">
          Inaugural cohort — one Founding Country Partner per country. Complimentary 12-month membership,
          permanent founding recognition, promotional priority during the launch period. Honest 7-stage
          tracking: prospects and unresponded invitations are <strong>never</strong> counted as members.
        </p>
      </header>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-6" data-testid="founding-stats">
          {STAGES.map((s) => (
            <button key={s} onClick={() => setFilter(s === filter ? "all" : s)}
              data-testid={`founding-stat-${s}`}
              className={`p-3 border text-left transition-colors ${filter === s ? "border-navy bg-navy/5" : "border-black/10 bg-white hover:border-navy/40"}`}>
              <div className="text-[10px] uppercase tracking-widest text-navy/60">{STAGE_LABELS[s]}</div>
              <div className="text-2xl font-display font-bold text-navy mt-1">{stats.counts_by_stage[s] || 0}</div>
            </button>
          ))}
        </div>
      )}

      {stats && (
        <div className="border border-black/10 bg-white p-4 mb-6 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid="founding-summary">
          <div><span className="text-navy/60">Publicly countable members:</span> <strong className="text-emerald-700">{stats.public_countable}</strong> of {stats.total_prospects} prospects</div>
          <div><span className="text-navy/60">Conversion:</span> <strong>{stats.conversion_rate.toFixed(1)}%</strong></div>
          <div className="text-navy/50 text-xs">Verified + Founding members only — prospects and invitations are never counted publicly.</div>
        </div>
      )}

      <div className="border border-black/10 bg-white">
        <div className="px-4 py-3 border-b border-black/10 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-navy/60">
            Showing <strong className="text-navy">{filtered.length}</strong> of {prospects.length} countries
            {filter !== "all" && <> · filter: <span className="uppercase tracking-widest">{STAGE_LABELS[filter]}</span> · <button onClick={() => setFilter("all")} className="underline">clear</button></>}
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-navy/50 text-sm">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/5 text-navy/70 uppercase text-xs tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">Country</th>
                  <th className="text-left px-4 py-3">Company · Contact</th>
                  <th className="text-left px-4 py-3">Source relationship</th>
                  <th className="text-left px-4 py-3">Stage</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-black/5 hover:bg-black/[0.02]" data-testid={`founding-row-${p.country}`}>
                    <td className="px-4 py-3 font-medium text-navy flex items-center gap-2">
                      <Flag size={14} weight="duotone" className="text-ember/70" /> {p.country}
                    </td>
                    <td className="px-4 py-3">
                      {p.company_name ? (
                        <>
                          <div className="font-medium">{p.company_name}</div>
                          <div className="text-xs text-navy/60">{p.contact_name} {p.contact_email && <>· {p.contact_email}</>}</div>
                        </>
                      ) : <span className="text-navy/40 italic text-xs">not identified yet</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-navy/70">{p.source_relationship || <span className="text-navy/30">—</span>}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest ${STAGE_COLOURS[p.stage] || "bg-black/5"}`}>{STAGE_LABELS[p.stage]}</span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(p)} data-testid={`founding-edit-${p.country}`}
                        className="text-navy/60 hover:text-ember mr-3" title="Edit prospect"><PencilSimple size={14} /></button>
                      {p.stage === "prospect" && (
                        <button onClick={() => sendInvite(p)} data-testid={`founding-invite-${p.country}`}
                          className="text-xs uppercase tracking-widest text-navy hover:text-ember mr-3">
                          <PaperPlaneTilt size={12} className="inline" /> Invite
                        </button>
                      )}
                      {p.stage === "invitation_sent" && (
                        <>
                          <button onClick={() => moveStage(p.id, "interested", "Prospect showed interest.")} className="text-emerald-700 hover:text-emerald-900 mr-2 text-xs uppercase tracking-widest" data-testid={`founding-interested-${p.country}`}>
                            <Check size={12} className="inline" /> Interested
                          </button>
                          <button onClick={() => moveStage(p.id, "declined", "Prospect declined.")} className="text-red-700 hover:text-red-900 text-xs uppercase tracking-widest" data-testid={`founding-decline-${p.country}`}>
                            <X size={12} className="inline" /> Declined
                          </button>
                        </>
                      )}
                      {p.stage === "interested" && (
                        <button onClick={() => moveStage(p.id, "accepted_terms", "Written acceptance received.")} className="text-xs uppercase tracking-widest text-navy hover:text-ember" data-testid={`founding-accepted-${p.country}`}>
                          Mark accepted
                        </button>
                      )}
                      {p.stage === "accepted_terms" && (
                        <button onClick={() => moveStage(p.id, "profile_verified", "Profile verified and activated.")} className="text-xs uppercase tracking-widest text-navy hover:text-ember" data-testid={`founding-verified-${p.country}`}>
                          Mark verified
                        </button>
                      )}
                      {p.stage === "profile_verified" && (
                        <button onClick={() => moveStage(p.id, "founding_member", "Activated as Founding Country Partner.")} className="text-xs uppercase tracking-widest bg-emerald-600 text-white px-2 py-1 hover:bg-emerald-700" data-testid={`founding-activate-${p.country}`}>
                          Activate founding member
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <EditModal prospect={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditModal({ prospect, onClose, onSaved }) {
  const [form, setForm] = useState({ ...prospect });
  const save = async () => {
    try {
      await api.patch(`/founding/prospects/${prospect.id}`, {
        company_name: form.company_name, contact_name: form.contact_name,
        contact_email: form.contact_email, website: form.website,
        source_relationship: form.source_relationship, licences: form.licences,
        coverage: form.coverage, notes: form.notes,
      });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" onClick={onClose} data-testid="founding-edit-modal">
      <div className="bg-white w-full max-w-2xl border border-black/10 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
          <div>
            <div className="overline">Founding Country Partner — {prospect.country}</div>
            <div className="font-display font-bold text-navy mt-1">Edit prospect</div>
          </div>
          <button onClick={onClose} className="text-navy/50 hover:text-navy text-2xl leading-none">×</button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Company"><input className="input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="founding-edit-company" /></Field>
          <Field label="Website"><input className="input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" /></Field>
          <Field label="Primary contact name"><input className="input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
          <Field label="Primary contact email"><input className="input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="founding-edit-email" /></Field>
          <Field label="Source relationship" full><input className="input" value={form.source_relationship} onChange={(e) => setForm({ ...form, source_relationship: e.target.value })} placeholder="e.g. PANCO co-member 2024-2026" /></Field>
          <Field label="Licences / certifications"><input className="input" value={form.licences} onChange={(e) => setForm({ ...form, licences: e.target.value })} /></Field>
          <Field label="Coverage / trade lanes"><input className="input" value={form.coverage} onChange={(e) => setForm({ ...form, coverage: e.target.value })} /></Field>
          <Field label="Notes" full><textarea className="input min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <div className="px-6 py-4 border-t border-black/10 flex justify-end gap-2">
          <button onClick={onClose} className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
          <button onClick={save} className="bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy/90" data-testid="founding-edit-save">Save</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, full, children }) => (
  <label className={`block text-xs uppercase tracking-widest text-navy/60 ${full ? "md:col-span-2" : ""}`}>
    {label}
    <div className="mt-1">{children}</div>
  </label>
);
